import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, DatePicker, Input, Modal, Select, message } from 'antd-v5';
import { QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getVendorItems, createInquiry, updateInquiry } from '../../../api/vendors';
import { getMerchantTags } from '../../../services/clients';
import { getRequest } from '../../../core/apiRequests';
import { SearchClientsInputV2 } from '../../CustomizedAntDesign/SearchClientsInputV2';
import './styles.scss';

const { TextArea } = Input;

const STATUS_OPTIONS = [
  { label: 'Waiting', value: 'WAITING' },
  { label: 'Ordered', value: 'ORDERED' },
  { label: 'Cancelled', value: 'CANCELLED' },
  { label: 'Fulfilled', value: 'FULFILLED' },
];

const AddRolexInquiryModal = ({ open, onCancel, onSave, initialModels, vendorId, auth, inquiry }) => {
  const isEditMode = !!inquiry;
  const [client, setClient] = useState(null);
  const [models, setModels] = useState([]);
  const [story, setStory] = useState('');
  const [tags, setTags] = useState([]);
  const [associate, setAssociate] = useState(null);
  const [inquiryDate, setInquiryDate] = useState(null);
  const [status, setStatus] = useState('WAITING');
  const [modelOptions, setModelOptions] = useState([]);
  const [modelSearchLoading, setModelSearchLoading] = useState(false);
  const [tagOptions, setTagOptions] = useState([]);
  const [associateOptions, setAssociateOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  const isManagerOrAdmin = useMemo(() => ['ADMIN', 'MANAGER'].includes(auth?.role), [auth?.role]);

  const currentUserName = useMemo(() => {
    return [auth?.firstName, auth?.lastName].filter(Boolean).join(' ') || '';
  }, [auth?.firstName, auth?.lastName]);

  useEffect(() => {
    if (open) {
      if (isEditMode) {
        setClient(inquiry.clientId ? { id: inquiry.clientId, name: inquiry.clientName } : null);
        setModels(inquiry.requestedProducts?.map(p => p.mpn) || inquiry.models || []);
        setStory(inquiry.story || '');
        setTags(
          (inquiry.tags || []).map(t => (typeof t === 'object' ? t.tagName || t.name : t)).filter(Boolean),
        );
        setStatus(inquiry.status || 'WAITING');
        setAssociate(
          inquiry.associateId
            ? { id: inquiry.associateId.toString(), name: inquiry.associateName || inquiry.associate }
            : null,
        );
        setInquiryDate(inquiry.inquiryDate ? dayjs(inquiry.inquiryDate) : dayjs());
      } else {
        setModels(initialModels?.length > 0 ? initialModels : []);
        setInquiryDate(dayjs());
        setAssociate(auth?.userId ? { id: auth.userId, name: currentUserName } : null);
      }
    }
  }, [open, initialModels, auth?.userId, currentUserName, isEditMode, inquiry]);

  useEffect(() => {
    if (!open) {
      setClient(null);
      setModels([]);
      setStory('');
      setTags([]);
      setAssociate(null);
      setInquiryDate(null);
      setStatus('WAITING');
      setModelOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      getMerchantTags({ query: { limit: 500 }, loadingStatusOff: true })
        .then(response => {
          const options = (response?.results || []).map(tag => ({
            label: tag.name,
            value: tag.name,
          }));
          setTagOptions(options);
        })
        .catch(() => setTagOptions([]));
    }
  }, [open]);

  useEffect(() => {
    if (open && isManagerOrAdmin && auth?.merchantId) {
      const fetchAssociates = async () => {
        const data = await getRequest({
          params: ['associates', auth.merchantId],
          query: { singleStore: 0 },
        });
        if (Array.isArray(data) && data.length > 0) {
          setAssociateOptions(
            data.map(a => ({
              value: a.id?.toString(),
              label: a.fullName || `${a.first_name || ''} ${a.last_name || ''}`.trim(),
            })),
          );
        }
      };
      fetchAssociates();
    }
  }, [open, isManagerOrAdmin, auth?.merchantId]);

  const handleModelSearch = useCallback(
    async searchValue => {
      if (!searchValue || searchValue.length < 2 || !vendorId) return;
      setModelSearchLoading(true);
      try {
        const response = await getVendorItems(vendorId, {
          params: { search: searchValue, limit: 20, offset: 0 },
        });
        const items = response?.data?.data || response?.data || [];
        const options = items.map(item => ({
          label: (
            <div className="add-rolex-inquiry-modal__model-option">
              {item.imageUrl && (
                <img className="add-rolex-inquiry-modal__model-image" src={item.imageUrl} alt={item.name} />
              )}
              <div className="add-rolex-inquiry-modal__model-info">
                <span className="add-rolex-inquiry-modal__model-name">{item.name}</span>
                <span className="add-rolex-inquiry-modal__model-mpn">{item.mpn}</span>
              </div>
            </div>
          ),
          value: item.mpn,
        }));
        setModelOptions(options);
      } catch {
        setModelOptions([]);
      } finally {
        setModelSearchLoading(false);
      }
    },
    [vendorId],
  );

  const handleSave = useCallback(async () => {
    if (!client) {
      message.error('Client is required');
      return;
    }
    if (models.length === 0) {
      message.error('At least one model is required');
      return;
    }
    if (isManagerOrAdmin && !associate) {
      message.error('Associate is required');
      return;
    }
    if (isManagerOrAdmin && !inquiryDate) {
      message.error('Inquiry date is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        clientId: client.id,
        models,
        story: story || undefined,
        tags: tags.length > 0 ? tags : undefined,
      };

      if (isManagerOrAdmin) {
        if (associate) payload.associateId = parseInt(associate.id, 10);
        if (inquiryDate) payload.inquiryDate = inquiryDate.format('YYYYMMDD');
        if (status) payload.status = status;
      }

      if (isEditMode) {
        await updateInquiry(inquiry.id, payload);
      } else {
        await createInquiry(payload);
      }

      message.success(isEditMode ? 'Inquiry updated' : 'Inquiry created');
      if (onSave) onSave();
      onCancel();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to save inquiry');
    } finally {
      setSaving(false);
    }
  }, [client, models, story, tags, associate, inquiryDate, status, isManagerOrAdmin, onSave, onCancel]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      closable={false}
      destroyOnClose
      width={520}
      className="add-rolex-inquiry-modal"
    >
      <div className="add-rolex-inquiry-modal__header">
        <h2 className="add-rolex-inquiry-modal__title">{isEditMode ? 'Edit Rolex Inquiry' : 'Add a Rolex Inquiry'}</h2>
        <Button type="primary" style={{ borderRadius: 8 }} onClick={handleSave} loading={saving}>
          Save
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>

      <div className="add-rolex-inquiry-modal__form" style={{ marginTop: 24 }}>
        <div className="add-rolex-inquiry-modal__field">
          <div className="add-rolex-inquiry-modal__label">
            Client <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }} />
            <span className="add-rolex-inquiry-modal__label-hint">(required)</span>
          </div>
          {isEditMode ? (
            <Input value={client?.name || ''} disabled />
          ) : (
            <SearchClientsInputV2 placeholder="Find a client" onSelect={setClient} />
          )}
        </div>

        <div className="add-rolex-inquiry-modal__field">
          <div className="add-rolex-inquiry-modal__label">
            {isManagerOrAdmin ? 'Rolex Model' : 'Models'}{' '}
            <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }} />
            <span className="add-rolex-inquiry-modal__label-hint">(required)</span>
          </div>
          <Select
            mode="multiple"
            placeholder="Search for a model"
            value={models}
            onChange={setModels}
            onSearch={handleModelSearch}
            loading={modelSearchLoading}
            options={modelOptions}
            optionLabelProp="value"
            filterOption={false}
            showSearch
            notFoundContent={modelSearchLoading ? 'Searching...' : 'Type to search models'}
          />
        </div>

        <div className="add-rolex-inquiry-modal__field">
          <div className="add-rolex-inquiry-modal__label">
            Story <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }} />
            <span className="add-rolex-inquiry-modal__label-hint">(optional)</span>
          </div>
          <TextArea rows={4} placeholder="Textarea" value={story} onChange={e => setStory(e.target.value)} />
        </div>

        <div className="add-rolex-inquiry-modal__field">
          <div className="add-rolex-inquiry-modal__label">
            Tags <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }} />
          </div>
          <Select
            mode="multiple"
            placeholder="Add tags"
            value={tags}
            onChange={setTags}
            options={tagOptions}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            showSearch
          />
        </div>

        {isManagerOrAdmin && (
          <>
            <div className="add-rolex-inquiry-modal__divider">MANAGERS AND ADMINS</div>

            <div className="add-rolex-inquiry-modal__field">
              <div className="add-rolex-inquiry-modal__label">
                Associate <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }} />
                <span className="add-rolex-inquiry-modal__label-hint">(required)</span>
              </div>
              <Select
                placeholder="Find a Sales Associate"
                value={associate?.id?.toString() || undefined}
                onChange={(val, option) => setAssociate({ id: val, name: option?.label })}
                options={associateOptions}
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                showSearch
                style={{ width: '100%' }}
              />
            </div>

            <div className="add-rolex-inquiry-modal__field">
              <div className="add-rolex-inquiry-modal__label">
                Inquiry Date <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }} />
                <span className="add-rolex-inquiry-modal__label-hint">(required)</span>
              </div>
              <DatePicker
                placeholder="Select date"
                value={inquiryDate}
                onChange={setInquiryDate}
                style={{ width: '100%' }}
              />
            </div>

            <div className="add-rolex-inquiry-modal__field">
              <div className="add-rolex-inquiry-modal__label">
                Status <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }} />
                <span className="add-rolex-inquiry-modal__label-hint">(required)</span>
              </div>
              <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} style={{ width: '100%' }} />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AddRolexInquiryModal);
